/**
 * Alert Component
 *
 * Custom alert component to replace Fluent UI MessageBar.
 * Eliminates ResizeObserver dependency while maintaining consistent styling.
 *
 * Uses Fluent UI design tokens for color consistency.
 */

import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import {
  ErrorCircle16Filled,
  CheckmarkCircle16Filled,
  Warning16Filled,
  Info16Filled,
} from '@fluentui/react-icons';

export interface AlertProps {
  intent: 'error' | 'success' | 'warning' | 'info';
  title?: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

const useStyles = makeStyles({
  alert: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.margin('8px', '0'),
    ...shorthands.gap('8px'),
    minHeight: '36px',
    color: tokens.colorNeutralForeground1,
  },
  alertError: {
    backgroundColor: tokens.colorStatusDangerBackground1,
  },
  alertSuccess: {
    backgroundColor: tokens.colorStatusSuccessBackground1,
  },
  alertWarning: {
    backgroundColor: tokens.colorStatusWarningBackground1,
  },
  alertInfo: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    paddingTop: '2px',
  },
  iconError: {
    color: tokens.colorStatusDangerForeground1,
  },
  iconSuccess: {
    color: tokens.colorStatusSuccessForeground1,
  },
  iconWarning: {
    color: tokens.colorStatusWarningForeground1,
  },
  iconInfo: {
    color: tokens.colorNeutralForeground2,
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    flexGrow: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    marginRight: '4px',
  },
  body: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
});

const iconComponents = {
  error: ErrorCircle16Filled,
  success: CheckmarkCircle16Filled,
  warning: Warning16Filled,
  info: Info16Filled,
};

export function Alert({ intent, title, children, 'data-testid': dataTestId }: AlertProps) {
  const styles = useStyles();

  const intentClass = {
    error: styles.alertError,
    success: styles.alertSuccess,
    warning: styles.alertWarning,
    info: styles.alertInfo,
  }[intent];

  const iconClass = {
    error: styles.iconError,
    success: styles.iconSuccess,
    warning: styles.iconWarning,
    info: styles.iconInfo,
  }[intent];

  const IconComponent = iconComponents[intent];

  return (
    <div
      role="alert"
      className={`${styles.alert} ${intentClass} alert-${intent}`}
      data-testid={dataTestId}
    >
      <span className={`${styles.icon} ${iconClass}`}>
        <IconComponent />
      </span>
      <div className={styles.content}>
        {title && <span className={styles.title}>{title}</span>}
        <span className={styles.body}>{children}</span>
      </div>
    </div>
  );
}
